// /reports — reportes contables derivados del ledger.
//
// Los filtros viven en la URL (no en useState) a proposito: un reporte
// financiero se comparte y se archiva. Con los params en el query string, el
// link que le mandas al contador abre exactamente lo que vos viste.

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getFinancialReport,
  getTrialBalance,
  listPartners,
  AdminApiError,
} from '@/lib/api';
import { downloadFinancialReportPdf } from '@/lib/financialReportPdf';
import StackedBarChart from '@/components/charts/StackedBarChart';
import type { StackedBarInput } from '@/lib/charts/geometry';
import { useSession } from '@/hooks/useSession';
import { fieldCls } from '@/lib/form-styles';

const today = (): string => new Date().toISOString().slice(0, 10);
const monthsAgo = (n: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

export default function Reports(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const session = useSession();
  const [downloading, setDownloading] = useState(false);

  const partnerId = params.get('partner_id') ?? '';
  const from = params.get('from') ?? monthsAgo(6);
  const to = params.get('to') ?? today();
  const groupBy = (params.get('group_by') ?? 'month') as 'day' | 'week' | 'month';
  const currency = params.get('currency') ?? 'ARS';

  const setParam = (k: string, v: string): void => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const partnersQ = useQuery({ queryKey: ['admin-partners'], queryFn: listPartners });

  const q = { partner_id: partnerId || undefined, from, to, group_by: groupBy, currency };
  const reportQ = useQuery({
    queryKey: ['admin-report-financial', q],
    queryFn: () => getFinancialReport(q),
  });
  const tbQ = useQuery({
    queryKey: ['admin-trial-balance', partnerId, to, currency],
    queryFn: () => getTrialBalance({ partner_id: partnerId || undefined, as_of: to, currency }),
  });

  const money = useMemo(
    () => (v: number) =>
      new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(v),
    [currency],
  );

  const report = reportQ.data;
  const chartData: StackedBarInput[] = (report?.buckets ?? []).map((b) => ({
    label: b.period,
    segments: [
      { key: 'net', value: b.net_collected },
      { key: 'vat', value: b.vat },
    ],
  }));

  const rec = report?.reconciliation;
  const untrustworthy = rec ? !rec.balanced || !rec.chains_verified : false;

  const partnerLabel =
    partnersQ.data?.find((p) => p.partner_id === partnerId)?.display_name ??
    (partnerId || 'Todos los partners');

  async function onDownload(): Promise<void> {
    if (!report || !tbQ.data) return;
    setDownloading(true);
    try {
      await downloadFinancialReportPdf({
        report,
        trialBalance: tbQ.data,
        partnerLabel,
        generatedBy: session?.display_name ?? 'admin',
      });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 data-help-page="reports.page" data-help="reports.page" className="text-xl font-semibold text-ink dark:text-ink-inverted">Reportes</h1>
          <p className="text-sm text-ink-muted">
            Derivados del libro contable, no de la tabla de pagos.
          </p>
        </div>
        <button
          type="button"
          data-help="reports.export"
          onClick={() => void onDownload()}
          disabled={!report || !tbQ.data || downloading}
          className="min-h-[2.75rem] rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
        >
          {downloading ? 'Generando…' : 'Bajar PDF'}
        </button>
      </div>

      {/* Filtros en una fila arriba de los charts. */}
      <div
        data-help="reports.filters"
        className="flex flex-wrap gap-3 rounded-lg border border-surface-border p-3 dark:border-surface-dark-border"
      >
        <Field label="Partner">
          <select
            value={partnerId}
            onChange={(e) => setParam('partner_id', e.target.value)}
            className={fieldCls({ block: false })}
          >
            <option value="">Todos</option>
            {partnersQ.data?.map((p) => (
              <option key={p.partner_id} value={p.partner_id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Desde">
          <input
            type="date"
            value={from}
            onChange={(e) => setParam('from', e.target.value)}
            className={fieldCls({ block: false })}
          />
        </Field>
        <Field label="Hasta">
          <input
            type="date"
            value={to}
            onChange={(e) => setParam('to', e.target.value)}
            className={fieldCls({ block: false })}
          />
        </Field>
        <Field label="Agrupar por">
          <select
            value={groupBy}
            onChange={(e) => setParam('group_by', e.target.value)}
            className={fieldCls({ block: false })}
          >
            <option value="day">Dia</option>
            <option value="week">Semana</option>
            <option value="month">Mes</option>
          </select>
        </Field>
      </div>

      {/* Si el ledger no cuadra o una cadena esta rota, se dice arriba de todo.
          Mostrar los numeros sin la advertencia seria mentir por omision. */}
      {untrustworthy && rec ? (
        <div
          data-help="reports.integrity"
          className="rounded-lg border border-red-500 bg-red-50 p-3 dark:bg-red-950/30"
        >
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">
            Estos numeros no son confiables
          </p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-400">
            {!rec.balanced
              ? `El libro no cuadra: debe ${money(rec.debit_total)} contra haber ${money(rec.credit_total)}. `
              : ''}
            {!rec.chains_verified
              ? 'Al menos una cadena de auditoria no verifica: el registro fue alterado por fuera de la aplicacion. '
              : ''}
            {rec.chains
              .filter((c) => !c.verified)
              .map((c) => `${c.chain_name}: ${c.detail ?? 'rota'}`)
              .join(' · ')}
          </p>
        </div>
      ) : null}

      {reportQ.isError ? (
        <p className="text-sm text-red-600">
          {reportQ.error instanceof AdminApiError ? reportQ.error.message : 'No se pudo cargar.'}
        </p>
      ) : null}
      {reportQ.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}

      {report ? (
        <>
          {/* Titular: el numero que importa es el DEVENGADO, no lo cobrado. */}
          <div data-help="reports.totals" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Cobrado bruto" value={money(report.totals.gross_collected)} note="incluye IVA" />
            <Tile label="IVA" value={money(report.totals.vat)} note="no es tuyo" />
            <Tile
              label="Ingreso devengado"
              value={money(report.totals.revenue_recognized)}
              note="servicio ya prestado"
              highlight
            />
            <Tile
              label="Diferido al cierre"
              value={money(report.totals.deferred_balance_end)}
              note="deuda con suscriptores"
            />
          </div>

          {/* Cuentas por origen del acceso. Snapshot a hoy (no al rango). Los
              cupones NO suman ingresos — este es su único lugar en el reporte. */}
          <section
            data-help="reports.accounts"
            className="rounded-lg border border-surface-border p-4 dark:border-surface-dark-border"
          >
            <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">
              Cuentas por origen
            </h2>
            <p className="mb-3 text-xs text-ink-muted">
              Suscripciones vivas a hoy. Las cuentas con cupón acceden gratis por tiempo limitado y
              no generan ingresos.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Tile label="Plan gratis" value={String(report.account_counts.free_plan)} />
              <Tile label="Pagando" value={String(report.account_counts.paying)} highlight />
              <Tile label="Con cupón" value={String(report.account_counts.coupon)} />
            </div>
          </section>

          <section className="rounded-lg border border-surface-border p-4 dark:border-surface-dark-border">
            <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">
              Cobrado por periodo
            </h2>
            <p className="mb-3 text-xs text-ink-muted">
              Cuanto entro y que parte es IVA. Ojo: cobrar no es ganar — el ingreso se devenga por
              el paso del tiempo (ver la tabla).
            </p>
            <StackedBarChart
              data={chartData}
              formatValue={money}
              seriesKeys={['net', 'vat']}
              ariaLabel="Cobrado bruto por periodo, dividido en neto e IVA"
            />
          </section>

          {/* La tabla es la vista accesible del chart + el relieve que exige el
              WARN de contraste de la paleta. No es redundante: es obligatoria. */}
          <section className="overflow-x-auto rounded-lg border border-surface-border dark:border-surface-dark-border">
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted dark:border-surface-dark-border">
                  <th className="p-2">Periodo</th>
                  <th className="p-2 text-right">Bruto</th>
                  <th className="p-2 text-right">IVA</th>
                  <th className="p-2 text-right">Neto</th>
                  <th className="p-2 text-right">Devengado</th>
                  <th className="p-2 text-right">Diferido</th>
                  <th className="p-2 text-right">Cobros</th>
                  <th className="p-2 text-right">Tasa</th>
                </tr>
              </thead>
              <tbody>
                {report.buckets.map((b) => (
                  <tr key={b.period} className="border-b border-surface-border/50 dark:border-surface-dark-border/50">
                    <td className="p-2 text-ink dark:text-ink-inverted">{b.period}</td>
                    <td className="p-2 text-right">{money(b.gross_collected)}</td>
                    <td className="p-2 text-right">{money(b.vat)}</td>
                    <td className="p-2 text-right">{money(b.net_collected)}</td>
                    <td className="p-2 text-right font-medium">{money(b.revenue_recognized)}</td>
                    <td className="p-2 text-right">{money(b.deferred_balance_end)}</td>
                    <td className="p-2 text-right">
                      {b.charges_succeeded}/{b.charges_succeeded + b.charges_failed}
                    </td>
                    <td className="p-2 text-right">
                      {b.success_rate === null ? '—' : `${(b.success_rate * 100).toFixed(1)}%`}
                    </td>
                  </tr>
                ))}
                {report.buckets.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-ink-muted">
                      Sin movimientos en el rango.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </section>

          {tbQ.data ? (
            <section className="overflow-x-auto rounded-lg border border-surface-border dark:border-surface-dark-border">
              <h2 className="p-3 text-sm font-semibold text-ink dark:text-ink-inverted">
                Balance de sumas y saldos al {tbQ.data.as_of}
              </h2>
              <table className="w-full min-w-[40rem] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted dark:border-surface-dark-border">
                    <th className="p-2">Cta</th>
                    <th className="p-2">Cuenta</th>
                    <th className="p-2 text-right">Debe</th>
                    <th className="p-2 text-right">Haber</th>
                    <th className="p-2 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {tbQ.data.accounts.map((a) => (
                    <tr key={a.account_code} className="border-b border-surface-border/50 dark:border-surface-dark-border/50">
                      <td className="p-2 font-mono text-xs">{a.account_code}</td>
                      <td className="p-2 text-ink dark:text-ink-inverted">{a.name}</td>
                      <td className="p-2 text-right">{money(a.debit_total)}</td>
                      <td className="p-2 text-right">{money(a.credit_total)}</td>
                      <td className="p-2 text-right font-medium">{money(a.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-surface-border dark:border-surface-dark-border">
                    <td className="p-2 font-semibold" colSpan={2}>
                      Totales
                    </td>
                    <td className="p-2 text-right font-semibold">{money(tbQ.data.totals.debit)}</td>
                    <td className="p-2 text-right font-semibold">{money(tbQ.data.totals.credit)}</td>
                    <td className="p-2 text-right font-semibold">
                      {tbQ.data.totals.balanced ? 'cuadra' : 'NO cuadra'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </section>
          ) : null}

          {rec ? (
            <section className="rounded-lg border border-surface-border p-4 text-xs dark:border-surface-dark-border">
              <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">
                Integridad del registro
              </h2>
              <p className="mt-1 text-ink-muted">
                Hashes de cierre al momento de generar el reporte. Si alguien altera una fila, la
                cadena deja de verificar.
              </p>
              <ul className="mt-2 space-y-1">
                {rec.chains.map((c) => (
                  <li key={c.chain_name} className="flex flex-wrap items-baseline gap-2">
                    <span className={c.verified ? 'text-emerald-700 dark:text-emerald-500' : 'text-red-600'}>
                      {c.verified ? '✓' : '×'}
                    </span>
                    <span className="text-ink dark:text-ink-inverted">{c.chain_name}</span>
                    <span className="text-ink-muted">
                      {c.last_seq !== null ? `${c.last_seq} registros` : ''}
                    </span>
                    <span className="break-all font-mono text-[10px] text-ink-muted">
                      {c.head_hash?.slice(0, 32)}…
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function Tile({
  label,
  value,
  note,
  highlight,
}: {
  label: string;
  value: string;
  note?: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlight
          ? 'border-brand bg-brand/5'
          : 'border-surface-border dark:border-surface-dark-border'
      }`}
    >
      <p className="text-xs uppercase tracking-wide text-ink-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-ink dark:text-ink-inverted">{value}</p>
      {note ? <p className="text-xs text-ink-muted">{note}</p> : null}
    </div>
  );
}
